"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TravelEstimates = exports.LocationArrival = void 0;
const class_transformer_1 = require("class-transformer");
const class_validator_1 = require("class-validator");
const type_graphql_1 = require("type-graphql");
const transformToDate_1 = require("../transformers/transformToDate");
const LocationPosition_1 = require("./LocationPosition");
let LocationArrival = class LocationArrival {
    locationId;
    locationName;
    isAirport;
    timezone;
    /**
     * Arrival time at the location in local time.
     * It is stored as a date suggesting it's in the UTC timezone, but it's actually in the local timezone as part of legacy
     * handling of the timezones.
     */
    arrivalAt;
    /**
     * Departure time at the location in local time.
     * It is stored as a date suggesting it's in the UTC timezone, but it's actually in the local timezone as part of legacy
     * handling of the timezones.
     */
    departureAt;
    distanceKm;
    travelTimeMinutes;
    imageUrl;
    duration;
    position;
};
exports.LocationArrival = LocationArrival;
__decorate([
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)(),
    __metadata("design:type", String)
], LocationArrival.prototype, "locationId", void 0);
__decorate([
    (0, type_graphql_1.Field)(),
    (0, class_validator_1.IsString)(),
    __metadata("design:type", String)
], LocationArrival.prototype, "locationName", void 0);
__decorate([
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, class_validator_1.IsBoolean)(),
    __metadata("design:type", Boolean)
], LocationArrival.prototype, "isAirport", void 0);
__decorate([
    (0, type_graphql_1.Field)(),
    (0, class_validator_1.IsString)(),
    __metadata("design:type", String)
], LocationArrival.prototype, "timezone", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => Date, { nullable: true }),
    (0, class_validator_1.IsDate)(),
    (0, class_transformer_1.Expose)(),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Object)
], LocationArrival.prototype, "arrivalAt", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => Date, { nullable: true }),
    (0, class_validator_1.IsDate)(),
    (0, class_transformer_1.Expose)(),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Object)
], LocationArrival.prototype, "departureAt", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], LocationArrival.prototype, "distanceKm", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], LocationArrival.prototype, "travelTimeMinutes", void 0);
__decorate([
    (0, type_graphql_1.Field)({ nullable: true }),
    (0, class_validator_1.IsString)(),
    (0, class_validator_1.IsOptional)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", String)
], LocationArrival.prototype, "imageUrl", void 0);
__decorate([
    (0, type_graphql_1.Field)(),
    (0, class_validator_1.IsNumber)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], LocationArrival.prototype, "duration", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => LocationPosition_1.LocationPosition, { nullable: true }),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", LocationPosition_1.LocationPosition)
], LocationArrival.prototype, "position", void 0);
exports.LocationArrival = LocationArrival = __decorate([
    (0, type_graphql_1.ObjectType)("LocationArrival"),
    (0, type_graphql_1.InputType)("LocationArrivalInput"),
    (0, class_transformer_1.Exclude)()
], LocationArrival);
/**
 * TravelEstimates encapsulates calculated details of travel.
 * This includes travel time, distance, arrival time of the whole trip, as well as a list of locations that
 * will be visited during the trip with estimated arrival times at each specified location.
 *
 * Main algorithm for calculation of travel time using travel data is located in:
 * `api/src/domain/content/travel-data/queries/calculate-travel-data/travel-data-algorithm.ts`
 *
 * Main util for calculating arrival times is located in:
 * `api/src/domain/content/travel-estimates/utils/calculate-arrival-times.ts`
 *
 * Legacy service that was the base of this class is located in `@legacy/services/CalculatorService.ts`
 */
let TravelEstimates = class TravelEstimates {
    /**
     * Travel time in minutes - this is the time spent on the road between locations,
     * it does not contain the time spent at locations.
     */
    travelTimeMinutes;
    /**
     * Travel time in minutes - this is the time spent on the way back without stops.
     *
     * Orders created before calculation was added has this field equal to travelTimeMinutes.
     */
    travelTimeWaybackMinutes;
    /**
     * Total time spent at locations in minutes - this is the cumulative time spent at all
     * locations during the journey.
     */
    timeAtLocationsMinutes;
    /**
     * Total distance travelled in kilometers.
     */
    distanceKm;
    /**
     * Total distance travelled in kilometers on the way back without stops.
     *
     * Orders created before calculation was added has this field equal to distanceKm.
     */
    distanceWaybackKm;
    /**
     * Estimated arrival time at the destination.
     * Stored as a date suggesting it's in the UTC timezone, but it's actually in the local timezone as part of legacy handling of the timezones.
     * It may be null if the departure time is not known yet.
     */
    arrivalAt;
    /**
     * A list of locations that will be visited during the trip with estimated arrival times and distances from each other.
     * The list is sorted by arrival time.
     */
    locationArrivals;
    /**
     * Hash of the locations of the order to check if the costly travel estimates need to be recalculated
     * Calculated by `getLocationsArrivalsHash` function
     */
    hash;
};
exports.TravelEstimates = TravelEstimates;
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], TravelEstimates.prototype, "travelTimeMinutes", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], TravelEstimates.prototype, "travelTimeWaybackMinutes", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], TravelEstimates.prototype, "timeAtLocationsMinutes", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], TravelEstimates.prototype, "distanceKm", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => type_graphql_1.Float),
    (0, class_validator_1.IsNumber)(),
    (0, class_validator_1.IsDefined)(),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Number)
], TravelEstimates.prototype, "distanceWaybackKm", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => Date, { nullable: true }),
    (0, class_validator_1.IsOptional)(),
    (0, class_validator_1.IsDate)(),
    (0, class_transformer_1.Expose)(),
    transformToDate_1.TransformToDate,
    __metadata("design:type", Object)
], TravelEstimates.prototype, "arrivalAt", void 0);
__decorate([
    (0, type_graphql_1.Field)(() => [LocationArrival]),
    (0, class_validator_1.IsArray)(),
    (0, class_transformer_1.Type)(() => LocationArrival),
    (0, class_validator_1.ValidateNested)({ each: true }),
    (0, class_transformer_1.Expose)(),
    __metadata("design:type", Array)
], TravelEstimates.prototype, "locationArrivals", void 0);
__decorate([
    (0, type_graphql_1.Field)(),
    (0, class_validator_1.IsString)(),
    __metadata("design:type", String)
], TravelEstimates.prototype, "hash", void 0);
exports.TravelEstimates = TravelEstimates = __decorate([
    (0, type_graphql_1.ObjectType)("TravelEstimates"),
    (0, type_graphql_1.InputType)("TravelEstimatesInput"),
    (0, class_transformer_1.Exclude)()
], TravelEstimates);
//# sourceMappingURL=TravelEstimates.js.map